/**
 * Zustand store for Media data.
 *
 * Background Tools (non-destructive):
 *  - originalUrl / processedUrl: when a background edit is applied, processedUrl
 *    becomes the active `url` and originalUrl preserves the source. Reverting
 *    restores `url` from originalUrl.
 *  - backgroundType: "transparent" | "color:#xxxxxx" | "gradient:..." | "template:id" | "image"
 *  - version: incremented on every edit so the storefront can bust caches.
 *  - lastModified: ISO timestamp of last edit.
 *  - Recycled originals live in `recycled` (soft-delete bin). They are not
 *    rendered anywhere on the storefront and can be restored or permanently
 *    deleted from the Media Manager UI.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const MEDIA_KEY = "khays_media";
const RECYCLED_KEY = "khays_media_recycled";

export type BackgroundType =
  | "transparent"
  | `color:${string}`
  | `gradient:${string}`
  | `template:${string}`
  | "image";

export interface MediaItem {
  id: string;
  url: string;
  type: "image" | "video";
  featured: boolean;
  order: number;
  /** Background-tools metadata (all optional, additive). */
  originalUrl?: string;
  processedUrl?: string;
  backgroundType?: BackgroundType;
  version?: number;
  lastModified?: string;
}

export interface RecycledMedia {
  id: string;
  url: string;            // the soft-deleted file URL (original or replaced)
  type: "image" | "video";
  reason: "background-replaced" | "manual";
  originalKey: string;    // mediaKey it came from
  originalItemId: string; // id of the surviving MediaItem (for "Restore over current")
  recycledAt: string;
}

export interface MediaData { [key: string]: MediaItem[]; }

function loadFromStorage(): MediaData {
  try { const s = localStorage.getItem(MEDIA_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
function loadRecycled(): RecycledMedia[] {
  try { const s = localStorage.getItem(RECYCLED_KEY); if (s) return JSON.parse(s); } catch {} return [];
}

interface MediaState {
  data: MediaData;
  recycled: RecycledMedia[];
  _rev: number;
  setData: (data: MediaData) => void;
  setRecycled: (r: RecycledMedia[]) => void;
  reload: () => void;
}

export const useMediaStore = create<MediaState>()(
  devtools(
    (set, get) => ({
      data: loadFromStorage(),
      recycled: loadRecycled(),
      _rev: 0,
      setData: (data) => {
        localStorage.setItem(MEDIA_KEY, JSON.stringify(data));
        set({ data, _rev: get()._rev + 1 });
      },
      setRecycled: (recycled) => {
        localStorage.setItem(RECYCLED_KEY, JSON.stringify(recycled));
        set({ recycled, _rev: get()._rev + 1 });
      },
      reload: () => set({ data: loadFromStorage(), recycled: loadRecycled(), _rev: get()._rev + 1 }),
    }),
    { name: "MediaStore" }
  )
);

function save(data: MediaData): void { useMediaStore.getState().setData(data); }
function load(): MediaData { return useMediaStore.getState().data; }

export function getMedia(key: string): MediaItem[] {
  return (load()[key] || []).sort((a, b) => a.order - b.order);
}

export function getFeaturedImage(key: string): MediaItem | null {
  const items = getMedia(key);
  return items.find(m => m.featured && m.type === "image") || items.find(m => m.type === "image") || null;
}

/**
 * Find any image media for a device, scanning all keys that start with the
 * device name. Used by storefront widgets (banners, flash-sale popup,
 * featured) to ensure a device image always surfaces even when the exact
 * variant/grade key has no dedicated media uploaded.
 */
export function findAnyDeviceImage(deviceName: string): MediaItem | null {
  if (!deviceName) return null;
  const all = load();
  const keys = Object.keys(all).filter(k => k === deviceName || k.startsWith(`${deviceName}|`));
  for (const k of keys) {
    const items = (all[k] || []).slice().sort((a, b) => a.order - b.order);
    const featured = items.find(m => m.featured && m.type === "image");
    if (featured) return featured;
    const firstImg = items.find(m => m.type === "image");
    if (firstImg) return firstImg;
  }
  return null;
}

export function addMedia(key: string, url: string, type: "image" | "video"): MediaItem {
  const data = { ...load() };
  if (!data[key]) data[key] = [];
  const existing = data[key];
  const item: MediaItem = {
    id: `m_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    url, type, featured: existing.length === 0 && type === "image", order: existing.length,
  };
  data[key] = [...existing, item];
  save(data);
  return item;
}

export function removeMedia(key: string, mediaId: string): void {
  const data = { ...load() };
  if (!data[key]) return;
  data[key] = data[key].filter(m => m.id !== mediaId);
  data[key].forEach((m, i) => { m.order = i; });
  if (data[key].length > 0 && !data[key].some(m => m.featured)) {
    const firstImage = data[key].find(m => m.type === "image");
    if (firstImage) firstImage.featured = true;
  }
  save(data);
}

export function setFeaturedMedia(key: string, mediaId: string): void {
  const data = { ...load() };
  if (!data[key]) return;
  data[key] = data[key].map(m => ({ ...m, featured: m.id === mediaId }));
  save(data);
}

export function reorderMedia(key: string, mediaIds: string[]): void {
  const data = { ...load() };
  if (!data[key]) return;
  const itemMap = new Map(data[key].map(m => [m.id, m]));
  data[key] = mediaIds.map((id, i) => { const item = itemMap.get(id); if (item) item.order = i; return item; }).filter(Boolean) as MediaItem[];
  save(data);
}

export function getAllMediaKeys(): string[] {
  return Object.keys(load());
}

/* =========================================================================
 *                           BACKGROUND-TOOLS API
 * ========================================================================= */

/**
 * Apply a background-edit result to an existing media item.
 * - Preserves the source as `originalUrl` (only set on the FIRST edit; future
 *   edits keep the truly-original reference).
 * - Promotes `processedUrl` to be the active `url`.
 * - Soft-deletes the previous active URL into the recycle bin (unless it is
 *   already the original we're preserving).
 */
export function applyBackgroundEdit(
  key: string,
  mediaId: string,
  processedUrl: string,
  backgroundType: BackgroundType,
): MediaItem | null {
  const data = { ...load() };
  if (!data[key]) return null;
  const idx = data[key].findIndex(m => m.id === mediaId);
  if (idx < 0) return null;
  const prev = data[key][idx];
  const wasOriginal = !prev.originalUrl;
  const trueOriginal = prev.originalUrl || prev.url;
  // Soft-delete the URL we're about to replace, but ONLY if it's not the
  // protected original (we never want to lose that).
  if (!wasOriginal && prev.url && prev.url !== trueOriginal) {
    pushToRecycleBin({
      url: prev.url,
      type: "image",
      reason: "background-replaced",
      originalKey: key,
      originalItemId: prev.id,
    });
  }
  const next: MediaItem = {
    ...prev,
    url: processedUrl,
    originalUrl: trueOriginal,
    processedUrl,
    backgroundType,
    version: (prev.version || 0) + 1,
    lastModified: new Date().toISOString(),
  };
  data[key] = data[key].map((m, i) => (i === idx ? next : m));
  save(data);
  return next;
}

/**
 * Attach an originalUrl + processedUrl pair to a freshly-uploaded item without
 * doing the URL swap dance. Used by the "Remove background on upload" flow:
 * we have both the raw upload URL and the cutout URL up front, so we just
 * record them on the item directly. Active `url` becomes the processedUrl.
 */
export function attachOriginalToMedia(
  key: string,
  mediaId: string,
  originalUrl: string,
  processedUrl: string,
  backgroundType: BackgroundType,
): MediaItem | null {
  const data = { ...load() };
  if (!data[key]) return null;
  const idx = data[key].findIndex(m => m.id === mediaId);
  if (idx < 0) return null;
  const prev = data[key][idx];
  const next: MediaItem = {
    ...prev,
    url: processedUrl,
    originalUrl,
    processedUrl,
    backgroundType,
    version: (prev.version || 0) + 1,
    lastModified: new Date().toISOString(),
  };
  data[key] = data[key].map((m, i) => (i === idx ? next : m));
  save(data);
  return next;
}

/** Revert a media item to its preserved original. Drops processed metadata. */
export function revertBackgroundEdit(key: string, mediaId: string): MediaItem | null {
  const data = { ...load() };
  if (!data[key]) return null;
  const idx = data[key].findIndex(m => m.id === mediaId);
  if (idx < 0) return null;
  const prev = data[key][idx];
  if (!prev.originalUrl) return prev; // nothing to revert
  // Recycle the processed file so the user can still permanently delete it.
  if (prev.processedUrl && prev.processedUrl !== prev.originalUrl) {
    pushToRecycleBin({
      url: prev.processedUrl,
      type: "image",
      reason: "background-replaced",
      originalKey: key,
      originalItemId: prev.id,
    });
  }
  const next: MediaItem = {
    ...prev,
    url: prev.originalUrl,
    processedUrl: undefined,
    backgroundType: undefined,
    version: (prev.version || 0) + 1,
    lastModified: new Date().toISOString(),
  };
  data[key] = data[key].map((m, i) => (i === idx ? next : m));
  save(data);
  return next;
}

/* ============================ Recycle Bin =============================== */

export function getRecycledMedia(): RecycledMedia[] {
  return useMediaStore.getState().recycled;
}

function pushToRecycleBin(entry: Omit<RecycledMedia, "id" | "recycledAt">) {
  const list = [...useMediaStore.getState().recycled];
  list.unshift({
    ...entry,
    id: `r_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    recycledAt: new Date().toISOString(),
  });
  // Cap at 200 most-recent entries to avoid unbounded growth in the synced blob.
  useMediaStore.getState().setRecycled(list.slice(0, 200));
}

/** Permanently remove an entry from the recycle bin. Caller is responsible for
 *  also deleting the underlying storage object if desired. */
export function removeFromRecycleBin(recycledId: string) {
  const list = useMediaStore.getState().recycled.filter(r => r.id !== recycledId);
  useMediaStore.getState().setRecycled(list);
}

/** Restore a recycled URL back over its source media item (becomes the active url). */
export function restoreRecycledMedia(recycledId: string): boolean {
  const r = useMediaStore.getState().recycled.find(x => x.id === recycledId);
  if (!r) return false;
  const data = { ...load() };
  const items = data[r.originalKey];
  if (!items) return false;
  const idx = items.findIndex(m => m.id === r.originalItemId);
  if (idx < 0) return false;
  const prev = items[idx];
  // Push the currently-active URL into the bin so we can swap back if needed.
  if (prev.url && prev.url !== r.url) {
    pushToRecycleBin({
      url: prev.url,
      type: prev.type,
      reason: "background-replaced",
      originalKey: r.originalKey,
      originalItemId: prev.id,
    });
  }
  data[r.originalKey] = items.map((m, i) => i === idx ? {
    ...m,
    url: r.url,
    processedUrl: r.url === m.originalUrl ? undefined : m.processedUrl,
    backgroundType: r.url === m.originalUrl ? undefined : m.backgroundType,
    version: (m.version || 0) + 1,
    lastModified: new Date().toISOString(),
  } : m);
  save(data);
  removeFromRecycleBin(recycledId);
  return true;
}
